import { useState } from 'react'
import { router } from '@inertiajs/react'
import { Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { fdeProjectPath, fdeRoutes } from '@/frontend/features/Fde/routes'
import type { ProjectTask, FdeCopy } from '@/frontend/features/Fde/types'
import type { ChecklistItem } from '@/frontend/features/Fde/projectTaskTypes'

interface Props {
  task: ProjectTask
  projectId: string
  copy: FdeCopy
  canManage: boolean
}
export default function TaskChecklist({ task, projectId, copy, canManage }: Props) {
  const [title, setTitle] = useState('')
  const [busy, setBusy] = useState(false)
  const items = task.checklist ?? []
  if (!canManage && !items.length) return null
  function save(checklist: ChecklistItem[], added = false) {
    if (busy) return
    // Server props win on reload. A stale edit is rejected, never replayed over
    // another person's checklist; the unsaved input remains for review.
    router.patch(
      fdeProjectPath(`${fdeRoutes.projectTasks}/${task.id}`, projectId),
      { project_task: { checklist, lock_version: task.lock_version } },
      {
        preserveScroll: true,
        preserveState: true,
        onStart: () => setBusy(true),
        onFinish: () => setBusy(false),
        onSuccess: (page) => {
          if (added && !(page.props.flash as { alert?: string } | undefined)?.alert) setTitle('')
        },
      },
    )
  }
  function add(event: React.FormEvent) {
    event.preventDefault()
    if (title.trim())
      save([...items, { id: crypto.randomUUID(), title: title.trim(), completed: false }], true)
  }
  return (
    <section aria-label={copy.management.checklist} className="mb-6 border-t border-border/60 pt-4">
      {items.length > 0 && (
        <h3 className="mb-2 flex items-center gap-2 text-xs font-medium">
          {copy.management.checklist}
          <span className="font-normal text-muted-foreground">
            {items.filter((item) => item.completed).length}/{items.length}
          </span>
        </h3>
      )}
      <div className="space-y-1">
        {items.map((item) => (
          <div key={item.id} className="group flex items-center gap-2 py-1.5">
            <label className="flex min-w-0 flex-1 cursor-pointer items-start gap-2.5 text-sm leading-5">
              <input
                type="checkbox"
                checked={item.completed}
                disabled={!canManage || busy}
                onChange={(event) =>
                  save(
                    items.map((entry) =>
                      entry.id === item.id ? { ...entry, completed: event.target.checked } : entry,
                    ),
                  )
                }
                className="mt-1 size-3.5 accent-primary"
              />
              <span className={item.completed ? 'text-muted-foreground line-through' : ''}>
                {item.title}
              </span>
            </label>
            {canManage && (
              <Button
                variant="ghost"
                size="icon-sm"
                disabled={busy}
                aria-label={`${copy.management.remove_checklist_item}: ${item.title}`}
                onClick={() => save(items.filter((entry) => entry.id !== item.id))}
                className="opacity-50 hover:opacity-100"
              >
                <X className="size-3" />
              </Button>
            )}
          </div>
        ))}
      </div>
      {canManage && (
        <form onSubmit={add} className="mt-2 flex items-center gap-2">
          <Input
            aria-label={copy.management.add_checklist_item}
            placeholder={copy.management.add_checklist_item}
            maxLength={300}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            disabled={busy}
            className="h-8 border-0 bg-muted/30 text-xs shadow-none"
          />
          <Button
            type="submit"
            variant="ghost"
            size="icon-sm"
            disabled={busy || !title.trim()}
            aria-label={copy.management.add_checklist_item}
          >
            <Plus className="size-4" />
          </Button>
        </form>
      )}
    </section>
  )
}
